import React from "react";

const isVariable = (v) => /^\{\{\d+\}\}$/.test(String(v || "").trim());

const MEDIA_HEIGHTS = { SHORT: "h-24", MEDIUM: "h-36", TALL: "h-52" };

const suggestionLabel = (s) => s?.reply?.plainText ?? s?.action?.plainText ?? "";

const suggestionIcon = (s) => {
  const a = s?.action;
  if (!a) return null;
  if (a.openUrl) return "🔗";
  if (a.dialerAction) return "📞";
  if (a.createCalendarEvent) return "📅";
  return null;
};

const Chips = ({ suggestions, stacked }) =>
  suggestions?.length ? (
    <div className={stacked ? "flex flex-col border-t border-gray-100" : "flex flex-wrap gap-1.5 mt-2"}>
      {suggestions.map((s, i) => (
        <span
          key={i}
          className={
            stacked
              ? "px-3 py-2 text-xs font-semibold text-[#1a73e8] text-center border-b border-gray-100 last:border-0 truncate"
              : "px-3 py-1 rounded-full border border-[#1a73e8]/40 text-xs font-semibold text-[#1a73e8] bg-white truncate max-w-full"
          }
        >
          {suggestionIcon(s) && <span className="mr-1">{suggestionIcon(s)}</span>}
          {suggestionLabel(s) || "Button"}
        </span>
      ))}
    </div>
  ) : null;

const Media = ({ media, className }) => {
  const url = media?.contentInfo?.fileUrl;
  if (!url) return null;
  const height = MEDIA_HEIGHTS[media.mediaHeight] || MEDIA_HEIGHTS.MEDIUM;
  if (isVariable(url) || !/^https?:\/\//i.test(url)) {
    return (
      <div className={`${className || height} bg-gray-100 flex items-center justify-center text-xs text-gray-400`}>
        {isVariable(url) ? `Media ${url}` : "Media"}
      </div>
    );
  }
  return (
    <div className={`${className || height} bg-gray-100 overflow-hidden`}>
      <img
        src={media.contentInfo.thumbnailUrl && !isVariable(media.contentInfo.thumbnailUrl) ? media.contentInfo.thumbnailUrl : url}
        alt=""
        className="w-full h-full object-cover"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    </div>
  );
};

const Card = ({ card = {}, horizontal, width }) => (
  <div
    className={`bg-white rounded-2xl border border-gray-200 overflow-hidden shrink-0 ${width || "w-full"} ${
      horizontal ? "flex" : ""
    }`}
  >
    <Media media={card.cardMedia} className={horizontal ? "w-2/5 min-h-[7rem]" : undefined} />
    <div className={horizontal ? "flex-1 min-w-0" : ""}>
      <div className="px-3 py-2">
        {card.cardTitle && (
          <div className="text-sm font-semibold text-gray-800 break-words whitespace-pre-wrap">{card.cardTitle}</div>
        )}
        {card.cardDescription && (
          <div className="text-xs text-gray-600 mt-0.5 break-words whitespace-pre-wrap">{card.cardDescription}</div>
        )}
        {!card.cardTitle && !card.cardDescription && (
          <div className="text-xs text-gray-300">Card text</div>
        )}
      </div>
      <Chips suggestions={card.suggestions} stacked />
    </div>
  </div>
);

const TemplatePreview = ({ data }) => {
  const content = data?.content || {};
  const standalone = content.richCardDetails?.standalone;
  const carousel = content.richCardDetails?.carousel;

  let body;
  if (standalone) {
    body = <Card card={standalone.content} horizontal={standalone.cardOrientation === "HORIZONTAL"} />;
  } else if (carousel) {
    const width = carousel.cardWidth === "SMALL_WIDTH" ? "w-[9.5rem]" : "w-[13rem]";
    body = (
      <div className="flex gap-2 overflow-x-auto pb-1 -mr-3 pr-3">
        {(carousel.contents || []).map((c, i) => (
          <Card key={i} card={c} width={width} />
        ))}
      </div>
    );
  } else {
    body = (
      <div>
        <div className="bg-white rounded-2xl rounded-tl-sm border border-gray-200 px-3 py-2 text-sm text-gray-800 whitespace-pre-wrap break-words">
          {content.plainText || <span className="text-gray-300">Message text</span>}
        </div>
        <Chips suggestions={content.suggestions} />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[300px] rounded-[2rem] border-[6px] border-gray-800 bg-gray-800 shadow-lg">
      <div className="rounded-[1.6rem] bg-[#f1f3f4] overflow-hidden">
        <div className="bg-white px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-[#00b6ce] text-white text-xs font-bold flex items-center justify-center">
            R
          </div>
          <div className="text-xs font-semibold text-gray-700">Your brand</div>
        </div>
        <div className="p-3 min-h-[340px] max-h-[520px] overflow-y-auto">{body}</div>
      </div>
    </div>
  );
};

export default TemplatePreview;
